import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useProducts } from "@/contexts/ProductsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { isOfferActive, calcDiscountedPrice } from "@/utils/offerUtils";
import { variantDisplayLabel } from "@/utils/variantLabel";

const CartHoverPreview = () => {
  const { items, totalItems, updateQuantity } = useCart();
  const { products } = useProducts();
  const { isAr } = useLanguage();

  const lines = items
    .map((it) => {
      const product = products.find((p) => p.id === it.productId);
      if (!product) return null;
      const variant =
        it.variantIndex !== null && product.sizeVariants
          ? product.sizeVariants[it.variantIndex]
          : null;
      const basePrice = variant ? variant.price : product.price;
      const offer = (variant?.offer && isOfferActive(variant.offer)) ? variant.offer : product.offer;
      const price = isOfferActive(offer) ? calcDiscountedPrice(basePrice, offer) : basePrice;
      const name = isAr ? product.nameAr : product.name;
      const variantLabel = variantDisplayLabel(variant, isAr, product);
      const image = variant?.image || product.image;
      return { it, product, variant, name, variantLabel, image, price };
    })
    .filter(Boolean) as Array<{
      it: typeof items[number]; product: any; variant: any; name: string;
      variantLabel: string | null; image: string; price: number;
    }>;

  const subtotal = lines.reduce((s, l) => s + l.price * l.it.quantity, 0);

  return (
    <div
      role="dialog"
      aria-label={isAr ? "نظرة سريعة على السلة" : "Cart preview"}
      className="absolute top-full end-0 mt-2 w-80 bg-background border border-primary/20 rounded-2xl shadow-[0_24px_60px_-18px_hsl(var(--foreground)/0.35)] opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible translate-y-2 scale-[0.98] group-hover:translate-y-0 group-hover:scale-100 transition-all duration-300 ease-out z-50"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-base font-semibold">
            {isAr ? "سلتك" : "Your Cart"}
          </h3>
          <span className="font-body text-xs text-muted-foreground">
            {totalItems} {isAr ? "عنصر" : totalItems === 1 ? "item" : "items"}
          </span>
        </div>

        {lines.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground py-6 text-center">
            {isAr ? "سلتك فارغة" : "Your cart is empty"}
          </p>
        ) : (
          <>
            <ul className="max-h-72 overflow-y-auto divide-y divide-border/60 -mx-1">
              {lines.map((l, idx) => (
                <li
                  key={`${l.it.productId}__${l.it.variantIndex ?? "d"}__${idx}`}
                  className="flex items-center gap-3 px-2 py-2.5 rounded-xl transition-colors duration-200 hover:bg-primary/5"
                >
                  <img
                    src={l.image}
                    alt=""
                    className="w-12 h-12 object-cover rounded-lg border border-border/60 flex-shrink-0 transition-transform duration-300 group-hover/line:scale-105"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm truncate">{l.name}</p>
                    {l.variantLabel && (
                      <p className="font-body text-[11px] text-muted-foreground truncate">{l.variantLabel}</p>
                    )}
                    <p className="font-serif text-xs text-primary mt-0.5 tabular-nums">
                      <span
                        key={l.it.quantity}
                        className="inline-block animate-scale-in font-semibold"
                      >
                        {l.it.quantity}
                      </span>{" "}
                      × {l.price.toFixed(0)} EGP
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateQuantity(l.it.productId, l.it.variantIndex, l.it.quantity + 1)}
                    aria-label={isAr ? `زيادة كمية ${l.name}` : `Increase quantity of ${l.name}`}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-primary/40 text-primary bg-background hover:bg-primary hover:text-primary-foreground hover:scale-110 hover:shadow-md active:scale-90 transition-all duration-200 ease-out"
                  >
                    <Plus size={14} strokeWidth={2.5}  aria-hidden="true" focusable="false" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/60">
              <span className="font-body text-xs uppercase tracking-wider text-muted-foreground">
                {isAr ? "الإجمالي" : "Subtotal"}
              </span>
              <span
                key={subtotal}
                className="font-serif text-sm font-semibold text-primary tabular-nums animate-scale-in"
              >
                {subtotal.toFixed(0)} EGP
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Link
                to="/cart"
                className="text-center font-body text-xs uppercase tracking-widest py-2.5 border border-primary/40 text-primary hover:bg-primary/10 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 rounded-full"
              >
                {isAr ? "عرض السلة" : "View Cart"}
              </Link>
              <Link
                to="/checkout"
                className="text-center font-body text-xs uppercase tracking-widest py-2.5 bg-foreground text-background hover:bg-primary hover:text-primary-foreground hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 transition-all duration-200 rounded-full"
              >
                {isAr ? "إتمام الشراء" : "Checkout"}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartHoverPreview;
